import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchWorkspaces } from '../../services/api/adminWorkspaces';
import { fetchRoles } from '../../services/api/adminRbacRoles';

const AddMembershipModal = ({ open, existingClientIds = [], onClose, onPick }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState('member');
  const [error, setError] = useState(null);
  const [roleOptions, setRoleOptions] = useState(['admin', 'member', 'viewer']);

  // Load live RbacRole list (system + custom). Owner excluded — admin can't
  // assign owner via this UI.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchRoles()
      .then((res) => {
        if (cancelled) return;
        // Backend returns { roles: [...] }
        const rows = res.data?.roles || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const codes = rows.map((r) => r.code).filter((c) => c && c !== 'owner');
        if (codes.length) setRoleOptions(codes);
      })
      .catch(() => {/* keep fallback */});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetchWorkspaces({ q: query });
        if (cancelled) return;
        const list = (res.data?.workspaces || []).filter((w) => !existingClientIds.includes(w.id));
        setResults(list);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, query, existingClientIds]);

  const handleAdd = () => {
    if (!picked) return;
    onPick({ client_id: picked.id, client_name: picked.name, role });
    setPicked(null); setQuery(''); setRole('member');
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-card rounded-lg w-full max-w-md p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground">Add user to workspace</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Workspace</label>
            <input
              type="text" value={query} onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
              placeholder="Type workspace name…"
              className="w-full text-sm border border-input rounded-lg px-3 py-2"
            />
            {!picked && results.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-border rounded-lg">
                {results.slice(0, 20).map((w) => (
                  <div
                    key={w.id}
                    onClick={() => { setPicked({ id: w.id, name: w.name }); setQuery(w.name); }}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                  >{w.name}</div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}
                    className="w-full text-sm border border-input rounded-lg px-3 py-2">
              {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {error && <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose}
                    className="px-4 py-2 text-sm text-foreground border border-input rounded-lg hover:bg-muted/50">
              Cancel
            </button>
            <button onClick={handleAdd} disabled={!picked}
                    className="px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 disabled:opacity-50">
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMembershipModal;
